import { type ComboboxItem, Stack } from "@mantine/core";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useConfiguratorComponents } from "../../../../hooks/useConfiguratorComponents";
import { GeneratorType } from "../../../../types/api.types";
import type { Person } from "../../../../utils/backend-types";
import { ObjectName } from "../../../../utils/enums";
import { FormFieldController } from "../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { CustomFormFieldWrapper } from "../../../BuildingBlocks/Forms/MantineFormFields/CustomFormFieldWrapper/CustomFormFieldWrapper";
import { ChangeableOperator } from "../../../PeriodBasedComponents/ChangeableOperator/ChangeableOperator";
import { convertOperatorsToChangeableFieldData } from "../../../PeriodBasedComponents/ChangeableOperator/utils/convertOperatorsToChangeableFieldData";
import {
  BIOMASSE_HAUPTBRENNSTOFF_CHOICES,
  GENERATOR_FORM_DATA,
  VERBRENNUNG_HAUPTBRENNSTOFF_CHOICES
} from "../GeneratorModal.constants";
import type { GeneratorFormSectionProps } from "../GeneratorModal.types";
import { SolarPremiumSection } from "./sections/SolarPremiumSection";

interface GeneratorGeneralTab extends GeneratorFormSectionProps {
  isPremium?: boolean;
  siteId?: number;
  persons: Array<Person>;
  isCommissioningNotRequired?: boolean;
}

function GeneratorGeneralTab({
  generatorForm,
  isPremium = false,
  siteId,
  persons,
  isCommissioningNotRequired
}: GeneratorGeneralTab) {
  const {
    control,
    watch,
    formState: { errors },
    setError,
    clearErrors
  } = generatorForm;
  const type = watch("type");
  const isCombined = watch("is_combined");
  const isOnboardingDone = watch("onboarding_done");
  const isPartialFeedInSite = watch("is_partial_feedin_site");
  const date_emsys_onboarding_start = watch(
    "emsys.date_emsys_onboarding_start"
  );
  const hasDateEmsysOnboardingStart = !!date_emsys_onboarding_start;
  const operatorPeriods = watch("operation_periods");
  const currentOperator =
    convertOperatorsToChangeableFieldData(operatorPeriods)?.at(-1)?.value;
  const isCurrentOperatorErloesmonitoringActive = currentOperator
    ? persons.find((person) => person.id === currentOperator)
        ?.isErloesmonitoringActive
    : false;

  const {
    data: connections,
    isLoading: connectionsIsLoading,
    error: connectionsError
  } = useConfiguratorComponents(ObjectName.Connection, siteId);
  const connectionsChoices: Array<ComboboxItem> = useMemo(
    () =>
      connections?.map((connection) => ({
        value: connection.id.toString(),
        label: connection.name
      })) ?? [],
    [connections]
  );

  return (
    <Stack gap={0}>
      <FormFieldController control={control} data={GENERATOR_FORM_DATA.name} />
      <FormFieldController control={control} data={GENERATOR_FORM_DATA.type} />
      <FormFieldController
        control={control}
        data={{
          ...GENERATOR_FORM_DATA.connection,
          disabled: connectionsIsLoading || !!connectionsError,
          data: connectionsChoices
        }}
        error={errors.connection || connectionsError?.message}
      />
      <Controller
        control={control}
        name={GENERATOR_FORM_DATA.operation_periods.name}
        render={({
          field: { onChange, value, name },
          fieldState: { error }
        }) => (
          <CustomFormFieldWrapper
            {...GENERATOR_FORM_DATA.operation_periods}
            error={error?.message}
          >
            <ChangeableOperator
              changeableOperatorHistory={value}
              isCombined={isCombined}
              isOnboardingDone={isOnboardingDone}
              isPartialFeedInSite={isPartialFeedInSite}
              persons={persons}
              onChange={(fieldData) => {
                clearErrors(name);
                onChange(fieldData);
              }}
              onError={(errorMessage) => {
                setError(
                  name,
                  { message: errorMessage },
                  { shouldFocus: true }
                );
              }}
            />
          </CustomFormFieldWrapper>
        )}
      />
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA["einheit.bruttoleistung"]}
      />
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA.installed_capacity}
      />
      <FormFieldController
        control={control}
        data={{
          ...GENERATOR_FORM_DATA.commissioning,
          required: !isCommissioningNotRequired // if there was a value before, there still needs to be a value
        }}
      />
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA.decommissioning}
      />
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA["einheit.im_mastr_registriert"]}
      />
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA["einheit.einheit_mastr_nummer"]}
      />
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA["einheit.name_stromerzeugungseinheit"]}
      />
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA["einheit.datum_erstmalige_inbetriebnahme"]}
      />
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA["einheit.einspeisungsart"]}
      />

      {type === GeneratorType.EEGAnlageWind && (
        <>
          <FormFieldController
            control={control}
            data={GENERATOR_FORM_DATA["einheit.typenbezeichnung"]}
          />
          <FormFieldController
            control={control}
            data={GENERATOR_FORM_DATA["einheit.seriennummer"]}
          />
        </>
      )}

      {type === GeneratorType.EEGAnlageSolar && (
        <FormFieldController
          control={control}
          data={
            GENERATOR_FORM_DATA[
              "einheit.zugeordnete_wirkleistung_wechselrichter"
            ]
          }
        />
      )}

      {(type === GeneratorType.EEGAnlageBiomasse ||
        type === GeneratorType.KWKAnlageBiomasse) && (
        <>
          <FormFieldController
            control={control}
            data={GENERATOR_FORM_DATA["einheit.technologie"]}
          />
          <FormFieldController
            control={control}
            data={BIOMASSE_HAUPTBRENNSTOFF_CHOICES["einheit.hauptbrennstoff"]}
          />
        </>
      )}
      {(type === GeneratorType.EEGAnlageGSGK ||
        type === GeneratorType.KWKAnlageGSGK) && (
        <>
          <FormFieldController
            control={control}
            data={GENERATOR_FORM_DATA["einheit.technologie"]}
          />
          <FormFieldController
            control={control}
            data={GENERATOR_FORM_DATA["einheit.energietraeger"]}
          />
        </>
      )}
      {type === GeneratorType.KWKAnlageVerbrennung && (
        <>
          <FormFieldController
            control={control}
            data={GENERATOR_FORM_DATA["einheit.technologie"]}
          />
          <FormFieldController
            control={control}
            data={
              VERBRENNUNG_HAUPTBRENNSTOFF_CHOICES["einheit.hauptbrennstoff"]
            }
          />
          <FormFieldController
            control={control}
            data={GENERATOR_FORM_DATA["einheit.energietraeger"]}
          />
        </>
      )}

      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA["einheit.sr_code"]}
      />

      <FormFieldController
        control={control}
        data={{
          ...GENERATOR_FORM_DATA["einheit.tr_id"],
          infoText: hasDateEmsysOnboardingStart
            ? "Die TR‑ID ist als eindeutige Identifier der Ausfallarbeit bereits im Prozess eingebunden und kann nicht mehr angepasst werden."
            : isCurrentOperatorErloesmonitoringActive
              ? "Die TR‑ID ist der eindeutige Identifier der Ausfallarbeit und wird im Gesamtprozess benötigt. Bitte die Eingabe sorgfältig prüfen."
              : undefined,
          disabled: hasDateEmsysOnboardingStart
        }}
      />
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA["einheit.beschreibung"]}
      />

      {type === GeneratorType.EEGAnlageSolar && isPremium && (
        <SolarPremiumSection generatorForm={generatorForm} />
      )}
    </Stack>
  );
}

export { GeneratorGeneralTab };
